import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Activity, ArrowRight, ChevronRight, Clock, Leaf } from 'lucide-react';
import data from '../../../../data/mcsa/games/gut-check.json';

interface GutCheckProps {
    onComplete: () => void;
}

type TBucketValue = 'strongly-agree' | 'slightly-agree' | 'slightly-disagree' | 'strongly-disagree';
type TAnim = 'enter' | 'throw-left' | 'throw-slight-left' | 'throw-slight-right' | 'throw-right' | 'timeout' | '';
type TPhase = 'loading' | 'intro' | 'playing' | 'results';

interface IStatement {
    id: number;
    text: string;
    isPositive: boolean;
}

interface IBucket {
    label: string;
    value: TBucketValue;
    icon: string;
}

const STATEMENTS = data.statements as IStatement[];
const BUCKETS = data.buckets as IBucket[];
const TIMER_SECONDS = (data.secondsPerCard as number) || 8;
const TIMER_CIRC = 2 * Math.PI * 22;
const GAUGE_CIRC = 2 * Math.PI * 70;

const ANIM_MAP: Record<string, TAnim> = {
    'strongly-agree': 'throw-left',
    'slightly-agree': 'throw-slight-left',
    'slightly-disagree': 'throw-slight-right',
    'strongly-disagree': 'throw-right',
    skipped: 'timeout',
};

const RISK_POINTS: Record<string, number> = {
    'strongly-agree-negative': 4,
    'slightly-agree-negative': 3,
    'slightly-disagree-negative': 1,
    'strongly-disagree-negative': 0,
    'strongly-agree-positive': 0,
    'slightly-agree-positive': 1,
    'slightly-disagree-positive': 3,
    'strongly-disagree-positive': 4,
    skipped: 2,
};

const ARCHETYPES = [
    {
        maxPercent: 20, title: 'Clear Water', subtitle: 'Calm, clear instincts', color: '#6FA98A',
        description: "Your instincts here are steady and well-tuned. You treat staying safe online as a shared, human thing — not just IT's job — and you meet the unexpected with calm curiosity rather than blind trust. That quiet vigilance makes everyone around you a little safer.",
        insights: [
            "You see safety as something everyone shares, not someone else's task.",
            'You meet surprises with gentle skepticism instead of automatic trust.',
            'You treat good habits as part of working well, not obstacles to it.',
            'Your steadiness quietly lowers the odds of a slip for the whole team.',
        ],
    },
    {
        maxPercent: 40, title: 'Still Forest', subtitle: 'Grounded, with a little haze', color: '#7BAF9E',
        description: 'You bring real care to how you move online, and it shows. A few small assumptions leave a bit of haze in otherwise clear air — nothing worrying, just worth noticing. A little attention closes those gaps easily.',
        insights: [
            'Your instincts are sound — lean on them, and gently double-check.',
            'Watch for quiet overconfidence in spotting things at a glance.',
            'Even trusted inboxes and secure-looking sites can carry surprises.',
            'Steady, everyday habits matter more than the occasional sharp moment.',
        ],
    },
    {
        maxPercent: 60, title: 'Shifting Mist', subtitle: 'Finding your footing', color: '#8AA7BE',
        description: "You've got good instincts in places, and a few assumptions are still settling. It isn't about knowing less — it's about the picture of how modern tricks actually work coming into focus. Noticing is the whole first step, and you're already taking it.",
        insights: [
            "Spam filters and polished websites aren't proof that something's safe.",
            'The confident and experienced are exactly who clever tricks aim for.',
            'A little humility is one of the strongest things you can carry.',
            "Even imperfect reminders surface risks you hadn't pictured.",
        ],
    },
    {
        maxPercent: 80, title: 'Rising Tide', subtitle: 'A few currents to steady', color: '#A89BB6',
        description: "Some of the currents here run a little strong — a sense that safety is someone else's job, or that you're too sharp to be caught. Those are gentle, human blind spots, and they're the very ones tricks are built around. Naming them is how you steady the water.",
        insights: [
            'Slips usually trace to everyday habits, not missing software.',
            "Tricks land most often on people sure they're immune.",
            "Private browsing and VPNs don't shield you from a convincing message.",
            'Guidelines exist because even sharp people slip under pressure.',
        ],
    },
    {
        maxPercent: 100, title: 'Open Shore', subtitle: 'Exposed, and worth tending', color: '#B58C93',
        description: "Right now a few instincts leave you more open than you'd like — and that's genuinely okay to see. This isn't about being careless; it's about the specific blind spots that tricks are designed to find. The most convincing ones work precisely because we're sure they won't. This is a kind moment to recalibrate.",
        insights: [
            'Nearly every big slip involves people, not just technology.',
            "\"I'm too sharp to be fooled\" is the most exposed spot of all.",
            'Convincing setups lean on confidence and everyday routine.',
            'One rushed click in an unguarded moment can ripple a long way.',
        ],
    },
];

const pad2 = (n: number) => String(n).padStart(2, '0');

const riskLabelFor = (pct: number) =>
    pct <= 20 ? 'Clear skies' : pct <= 40 ? 'Mostly clear' : pct <= 60 ? 'A little misty' : pct <= 80 ? 'Choppy water' : 'Open water';

export const GutCheck = ({ onComplete }: GutCheckProps) => {
    const [gamePhase, setGamePhase] = useState<TPhase>('loading');
    const [queue, setQueue] = useState<IStatement[]>(STATEMENTS);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [pass, setPass] = useState<1 | 2>(1);
    const [answers, setAnswers] = useState<Record<number, string>>({});
    const [skippedFirst, setSkippedFirst] = useState<number[]>([]);
    const [timeLeft, setTimeLeft] = useState(TIMER_SECONDS);
    const [anim, setAnim] = useState<TAnim>('enter');
    const [locked, setLocked] = useState(false);
    const lockedRef = useRef(false);
    const answersRef = useRef(answers);
    const skippedRef = useRef(skippedFirst);
    const queueRef = useRef(queue);
    const indexRef = useRef(currentIndex);
    const passRef = useRef(pass);

    answersRef.current = answers;
    skippedRef.current = skippedFirst;
    queueRef.current = queue;
    indexRef.current = currentIndex;
    passRef.current = pass;

    useEffect(() => {
        if (gamePhase !== 'loading') return;
        const id = setTimeout(() => setGamePhase('intro'), 1800);
        return () => clearTimeout(id);
    }, [gamePhase]);

    const current = queue[currentIndex];
    const totalQueue = queue.length;
    const baseCount = pass === 1 ? 15 : totalQueue;
    const displayIndex = pass === 1 ? Math.min(currentIndex + 1, 15) : currentIndex + 1;
    const progressPct = totalQueue > 0 ? Math.min((currentIndex / totalQueue) * 100, 100) : 0;

    const result = useMemo(() => {
        let totalRisk = 0;
        let answeredCount = 0;
        let skippedCount = 0;
        const maxRisk = STATEMENTS.length * 4;
        for (const q of STATEMENTS) {
            const response = answers[q.id];
            if (!response || response === 'skipped') {
                skippedCount++;
                totalRisk += RISK_POINTS.skipped;
            } else {
                answeredCount++;
                const key = `${response}-${q.isPositive ? 'positive' : 'negative'}`;
                totalRisk += RISK_POINTS[key] ?? 2;
            }
        }
        const riskPercentage = Math.round((totalRisk / maxRisk) * 100);
        const archetype = ARCHETYPES.find((a) => riskPercentage <= a.maxPercent) || ARCHETYPES[ARCHETYPES.length - 1];
        return { riskPercentage, answeredCount, skippedCount, archetype };
    }, [answers]);

    const [gaugeOffset, setGaugeOffset] = useState(GAUGE_CIRC);
    useEffect(() => {
        if (gamePhase !== 'results') return;
        const id = setTimeout(() => {
            setGaugeOffset(GAUGE_CIRC * (1 - result.riskPercentage / 100));
        }, 600);
        return () => clearTimeout(id);
    }, [gamePhase, result.riskPercentage]);

    const advanceAfter = useCallback((nextAnswers: Record<number, string>, nextSkipped: number[]) => {
        const idx = indexRef.current;
        const q = queueRef.current;
        const p = passRef.current;
        const nextIndex = idx + 1;
        if (nextIndex >= q.length) {
            if (p === 1 && nextSkipped.length > 0) {
                const revisit = STATEMENTS.filter((s) => nextSkipped.includes(s.id));
                setQueue([...q, ...revisit]);
                setCurrentIndex(nextIndex);
                setPass(2);
                setAnswers(nextAnswers);
                setSkippedFirst(nextSkipped);
                setLocked(false);
                lockedRef.current = false;
                setAnim('enter');
                setTimeLeft(TIMER_SECONDS);
                return;
            }
            setAnswers(nextAnswers);
            setGamePhase('results');
            return;
        }
        setAnswers(nextAnswers);
        setSkippedFirst(nextSkipped);
        setCurrentIndex(nextIndex);
        setLocked(false);
        lockedRef.current = false;
        setAnim('enter');
        setTimeLeft(TIMER_SECONDS);
    }, []);

    const commit = useCallback((value: string) => {
        if (lockedRef.current) return;
        const q = queueRef.current[indexRef.current];
        if (!q) return;
        lockedRef.current = true;
        setLocked(true);
        setAnim(ANIM_MAP[value] || 'timeout');
        const nextAnswers = { ...answersRef.current };
        let nextSkipped = skippedRef.current;
        if (value === 'skipped') {
            if (passRef.current === 1) nextSkipped = [...nextSkipped, q.id];
        } else {
            nextAnswers[q.id] = value;
        }
        window.setTimeout(() => advanceAfter(nextAnswers, nextSkipped), value === 'skipped' ? 600 : 480);
    }, [advanceAfter]);

    useEffect(() => {
        if (gamePhase !== 'playing' || locked) return;
        setTimeLeft(TIMER_SECONDS);
        const interval = window.setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    commit('skipped');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [gamePhase, currentIndex, locked, commit]);

    const urgency = timeLeft <= 2 ? 'critical' : timeLeft <= 3 ? 'warning' : 'normal';
    const ringColor = urgency === 'critical' ? '#7FB09A' : urgency === 'warning' ? '#6E93B0' : '#566B87';
    const dashOffset = TIMER_CIRC * (1 - timeLeft / TIMER_SECONDS);

    if (gamePhase === 'loading') {
        return (
            <div className="mcsa_game__gut_check mcsa_game__gut_check--center">
                <div className="mcsa_game__gut_check__spin">
                    <span className="mcsa_game__gut_check__spin_ring" />
                    <Leaf size={24} />
                </div>
                <p className="mcsa_game__gut_check__eyebrow">Getting ready</p>
                <h2 className="mcsa_game__gut_check__h3">Settling in…</h2>
            </div>
        );
    }

    if (gamePhase === 'intro') {
        return (
            <div className="mcsa_game__gut_check mcsa_game__gut_check--center">
                <div className="mcsa_game__gut_check__intro_card">
                    <div className="mcsa_game__gut_check__hero" />
                    <div className="mcsa_game__gut_check__hero_icon"><Activity size={26} /></div>
                    <div className="mcsa_game__gut_check__intro_body">
                        <h1 className="mcsa_game__gut_check__h1">Gut Check: The Oracle</h1>
                        <p className="mcsa_game__gut_check__body">
                            A quiet read on how you think about staying safe online. Fifteen short statements, 8 seconds each. No studying, no right answers. Just go with your gut.
                        </p>
                        <div className="mcsa_game__gut_check__meta">
                            <span><Clock size={15} /> Take your time</span>
                            <span>·</span>
                            <span>15 quick statements</span>
                            <span>·</span>
                            <span><Leaf size={15} /> Trust your gut</span>
                        </div>
                        <button
                            type="button"
                            className="mcsa_game__gut_check__cta"
                            onClick={() => {
                                setQueue(STATEMENTS);
                                setCurrentIndex(0);
                                setPass(1);
                                setAnswers({});
                                setSkippedFirst([]);
                                setGamePhase('playing');
                            }}
                        >
                            Let’s check in <ArrowRight size={18} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (gamePhase === 'results') {
        const { archetype, riskPercentage, answeredCount, skippedCount } = result;
        return (
            <div className="mcsa_game__gut_check mcsa_game__gut_check--results">
                <div className="mcsa_game__gut_check__icon_circle"><Leaf size={24} /></div>
                <p className="mcsa_game__gut_check__eyebrow">Check-in complete</p>
                <div className="mcsa_game__gut_check__gauge">
                    <svg width="180" height="180" viewBox="0 0 180 180">
                        <circle cx="90" cy="90" r="70" fill="none" stroke="rgba(86,107,135,0.1)" strokeWidth="12" />
                        <circle
                            cx="90" cy="90" r="70" fill="none" stroke={archetype.color} strokeWidth="12" strokeLinecap="round"
                            strokeDasharray={GAUGE_CIRC} strokeDashoffset={gaugeOffset}
                            style={{ transform: 'rotate(-90deg)', transformOrigin: '90px 90px', transition: 'stroke-dashoffset 1.5s cubic-bezier(0.4,0,0.2,1)' }}
                        />
                        <text x="90" y="82" textAnchor="middle" fill={archetype.color} fontSize="28" fontWeight="700">{riskPercentage}%</text>
                        <text x="90" y="104" textAnchor="middle" fill="#6E7C8A" fontSize="12" fontWeight="600">{riskLabelFor(riskPercentage)}</text>
                    </svg>
                </div>
                <div className="mcsa_game__gut_check__glass">
                    <p className="mcsa_game__gut_check__kicker">Where you are today</p>
                    <h2 className="mcsa_game__gut_check__h1">{archetype.title}</h2>
                    <p className="mcsa_game__gut_check__subtitle">{archetype.subtitle}</p>
                    <p className="mcsa_game__gut_check__body">{archetype.description}</p>
                </div>
                <div className="mcsa_game__gut_check__glass">
                    <h3 className="mcsa_game__gut_check__insights_h">A few gentle notes</h3>
                    <ul className="mcsa_game__gut_check__insights">
                        {archetype.insights.map((insight) => (
                            <li key={insight}>
                                <ChevronRight size={18} />
                                <span>{insight}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <p className="mcsa_game__gut_check__meta">{answeredCount} answered · {skippedCount} skipped</p>
                <button type="button" className="mcsa_game__gut_check__cta" onClick={onComplete}>
                    Continue <ArrowRight size={18} />
                </button>
            </div>
        );
    }

    if (!current) return null;

    return (
        <div className="mcsa_game__gut_check">
            <header className="mcsa_game__gut_check__header">
                <div className="mcsa_game__gut_check__progress">
                    <div className="mcsa_game__gut_check__progress_meta">
                        <span>{pass === 2 ? 'Revisiting' : 'Statement'}</span>
                        <span>{displayIndex} / {baseCount}</span>
                    </div>
                    <div className="mcsa_game__gut_check__track">
                        <div className="mcsa_game__gut_check__fill" style={{ width: `${progressPct}%` }} />
                    </div>
                </div>
            </header>
            <main className="mcsa_game__gut_check__main">
                <div
                    className={`mcsa_game__gut_check__timer ${urgency === 'critical' ? 'mcsa_game__gut_check__timer--pulse' : ''}`}
                    style={{ background: urgency === 'critical' ? 'rgba(127,176,154,0.14)' : urgency === 'warning' ? 'rgba(110,147,176,0.12)' : 'rgba(86,107,135,0.1)' }}
                >
                    <svg className="mcsa_game__gut_check__timer_svg" width="48" height="48" viewBox="0 0 56 56">
                        <circle cx="28" cy="28" r="22" fill="none" stroke="rgba(86,107,135,0.12)" strokeWidth="3" />
                        <circle
                            cx="28" cy="28" r="22" fill="none" stroke={ringColor} strokeWidth="3" strokeLinecap="round"
                            strokeDasharray={TIMER_CIRC} strokeDashoffset={dashOffset}
                        />
                    </svg>
                    <span style={{ color: ringColor }}>{timeLeft}</span>
                </div>
                <div className={`mcsa_game__gut_check__card mcsa_game__gut_check__card--${anim}`}>
                    <div className="mcsa_game__gut_check__card_head">
                        <span className="mcsa_game__gut_check__badge">{pad2(currentIndex + 1)} / {pad2(totalQueue)}</span>
                        {pass === 2 && <span className="mcsa_game__gut_check__revisit">Revisit</span>}
                    </div>
                    <p className="mcsa_game__gut_check__card_text">{current.text}</p>
                </div>
                <div className="mcsa_game__gut_check__buckets">
                    {BUCKETS.map((bucket) => (
                        <button
                            key={bucket.value}
                            type="button"
                            className="mcsa_game__gut_check__bucket"
                            disabled={locked}
                            aria-label={bucket.label}
                            onClick={() => commit(bucket.value)}
                        >
                            <span className="mcsa_game__gut_check__bucket_icon">{bucket.icon}</span>
                            <span>{bucket.label}</span>
                        </button>
                    ))}
                </div>
                <p className="mcsa_game__gut_check__caption">Go with your gut — there’s no wrong answer.</p>
            </main>
        </div>
    );
};
